/**
 * Support for conveying general queries and query results between client and server.
 */
import { toLocalDate } from '../shared/date_tools';
export const EARLIEST_RECORD_DATE = new Date('1/1/1930');
export var QueryColumnID;
(function (QueryColumnID) {
    QueryColumnID["CatalogNumber"] = "catalog number";
    QueryColumnID["CollectionStartDate"] = "start date";
    QueryColumnID["CollectionEndDate"] = "end date";
    QueryColumnID["Locality"] = "locality";
    QueryColumnID["County"] = "county";
    QueryColumnID["Latitude"] = "latitude";
    QueryColumnID["Longitude"] = "longitude";
    QueryColumnID["IsAquaticKarst"] = "aquatic karst";
    QueryColumnID["IsTerrestrialKarst"] = "terrestrial karst";
    QueryColumnID["TypeStatus"] = "type status";
    QueryColumnID["Phylum"] = "phylum";
    QueryColumnID["Class"] = "class";
    QueryColumnID["Order"] = "order";
    QueryColumnID["Family"] = "family";
    QueryColumnID["Genus"] = "genus";
    QueryColumnID["Subgenus"] = "subgenus";
    QueryColumnID["Species"] = "species epithet";
    QueryColumnID["Subspecies"] = "subspecies epithet";
    QueryColumnID["TaxonUnique"] = "scientific name";
    QueryColumnID["KarstObligate"] = "karst obligate";
    QueryColumnID["IsFederallyListed"] = "federally listed";
    QueryColumnID["TexasStateRank"] = "Texas state rank";
    QueryColumnID["TpwdStatus"] = "TPWD status";
    QueryColumnID["SpecimenCount"] = "specimen count";
    QueryColumnID["LifeStage"] = "life stage";
    QueryColumnID["Collectors"] = "collectors";
    QueryColumnID["LocalityNotes"] = "locality notes";
    QueryColumnID["Determiners"] = "determiners";
    QueryColumnID["DeterminationYear"] = "determination year";
    QueryColumnID["DeterminationNotes"] = "determination notes";
    QueryColumnID["SpecimenNotes"] = "specimen notes";
    QueryColumnID["RecordCount"] = "record count";
    QueryColumnID["Problems"] = "problems";
})(QueryColumnID || (QueryColumnID = {}));
// @ts-ignore as the column IDs will soon be added
export const columnInfoMap = {};
const nullableOptions = [
    // postgres won't crash checking for null on non-nullables
    { text: 'Any value', sql: null },
    { text: 'Non-blank', sql: 'X is not null' },
    { text: 'Blank', sql: 'X is null' }
];
const yesNoOptions = [
    { text: 'Any value', sql: null },
    { text: 'Yes', sql: 'X = true' },
    { text: 'No', sql: 'X = false' }
];
const setColumnInfo = (columnInfo) => {
    columnInfoMap[columnInfo.columnID] = columnInfo;
};
const getDateValue = (date) => {
    return date ? toLocalDate(new Date(date)) : '';
};
const getNames = (names) => (names ? names.replace('|', '; ') : '');
const getNumber = (num) => (num ? num.toString() : '');
setColumnInfo({
    columnID: QueryColumnID.RecordCount,
    fullName: 'Record Count',
    abbrName: 'Records',
    description: 'Number of specimen records associated with the resulting row.',
    column1: 'count(*)',
    asName: 'record_count',
    options: null,
    defaultEmWidth: 5,
    columnClass: 'center',
    getValue: (row) => getNumber(row.recordCount)
});
setColumnInfo({
    columnID: QueryColumnID.CatalogNumber,
    fullName: 'Catalog Number',
    abbrName: 'Catalog No.',
    description: "Catalog number of the specimen(s) in UT Austin's Specify database.",
    column1: 'catalog_number',
    column2: 'occurrence_guid',
    defaultEmWidth: 6,
    options: null,
    columnClass: null,
    getValue: (row) => row.catalogNumber || ''
});
setColumnInfo({
    columnID: QueryColumnID.CollectionStartDate,
    fullName: 'Date Collection Started',
    abbrName: 'Start Date',
    description: 'First day of collection, which may be the only collection date',
    column1: 'collection_start_date',
    column2: 'partial_start_date',
    defaultEmWidth: 6,
    options: nullableOptions,
    columnClass: 'center',
    getValue: (row) => row.partialStartDate
        ? _toDisplayedPartialDate(row.partialStartDate)
        : getDateValue(row.collectionStartDate)
});
setColumnInfo({
    columnID: QueryColumnID.CollectionEndDate,
    fullName: 'Date Collection Ended',
    abbrName: 'End Date',
    description: 'Last day of collection, if collected over more than one day',
    column1: 'collection_end_date',
    column2: 'partial_end_date',
    options: nullableOptions,
    defaultEmWidth: 6,
    columnClass: 'center',
    getValue: (row) => {
        if (row.partialEndDate)
            return _toDisplayedPartialDate(row.partialEndDate);
        // Don't return a blank end date, because we don't know when the end date was,
        // and because the user might get confused over why the record doesn't show
        // in blank end date searches.
        if (row.partialStartDate)
            return _toDisplayedPartialDate(row.partialStartDate);
        return getDateValue(row.collectionEndDate);
    }
});
setColumnInfo({
    columnID: QueryColumnID.Collectors,
    fullName: 'Collector Names',
    abbrName: 'Collectors',
    description: 'Names of the participating collectors',
    column1: 'collectors',
    options: nullableOptions,
    defaultEmWidth: 16,
    columnClass: null,
    getValue: (row) => getNames(row.collectors)
});
setColumnInfo({
    columnID: QueryColumnID.Determiners,
    fullName: 'Determiner Names',
    abbrName: 'Determiners',
    description: 'Names of the determiners',
    column1: 'determiners',
    options: nullableOptions,
    defaultEmWidth: 10,
    columnClass: null,
    getValue: (row) => getNames(row.determiners)
});
setColumnInfo({
    columnID: QueryColumnID.DeterminationYear,
    fullName: 'Determination Year',
    abbrName: 'Det. Year',
    description: 'Names of the determiners',
    column1: 'determination_year',
    options: nullableOptions,
    defaultEmWidth: 6,
    columnClass: 'center',
    getValue: (row) => getNumber(row.determinationYear)
});
setColumnInfo({
    columnID: QueryColumnID.LocalityNotes,
    fullName: 'Locality & Habitat Notes',
    abbrName: null,
    description: 'Notes on the location and habitat',
    column1: 'locality_notes',
    options: nullableOptions,
    defaultEmWidth: 16,
    columnClass: null,
    getValue: (row) => row.localityNotes || ''
});
setColumnInfo({
    columnID: QueryColumnID.SpecimenNotes,
    fullName: 'Specimen Notes',
    abbrName: null,
    description: 'Notes about the specimens collected',
    column1: 'specimen_notes',
    options: nullableOptions,
    defaultEmWidth: 16,
    columnClass: null,
    getValue: (row) => row.specimenNotes || ''
});
setColumnInfo({
    columnID: QueryColumnID.DeterminationNotes,
    fullName: 'Determination Notes',
    abbrName: 'Det. Notes',
    description: 'Notes on the determination',
    column1: 'determination_notes',
    options: nullableOptions,
    defaultEmWidth: 12,
    columnClass: null,
    getValue: (row) => row.determinationNotes || ''
});
setColumnInfo({
    columnID: QueryColumnID.TypeStatus,
    fullName: 'Type Status',
    abbrName: null,
    description: 'Type status of this particular specimen, and whether it is undescribed.',
    column1: 'type_status',
    options: [
        { text: 'Any value', sql: null },
        { text: 'Undescribed', sql: "X='undescribed'" },
        { text: 'Any type', sql: "X is not null and X!='undescribed'" },
        { text: 'Non-blank', sql: 'X is not null' },
        { text: 'Blank', sql: 'X is null' }
    ],
    defaultEmWidth: 7,
    columnClass: null,
    getValue: (row) => row.typeStatus || ''
});
setColumnInfo({
    columnID: QueryColumnID.SpecimenCount,
    fullName: 'Specimen Count',
    abbrName: 'Specimens',
    description: 'The number of specimens collected',
    column1: 'specimen_count',
    options: nullableOptions,
    defaultEmWidth: 6,
    columnClass: 'center',
    getValue: (row) => row.specimenCount || ''
});
setColumnInfo({
    columnID: QueryColumnID.LifeStage,
    fullName: 'Life Stage',
    abbrName: 'Stage',
    description: 'Life stage of specimens (adult = includes adults; blank = unknown)',
    column1: 'life_stage',
    options: [
        { text: 'Any value', sql: null },
        { text: 'Adult', sql: "X='adult'" },
        { text: 'Immature', sql: "X is not null and X!='adult'" },
        { text: 'Non-blank', sql: 'X is not null' },
        { text: 'Blank', sql: 'X is null' }
    ],
    defaultEmWidth: 4,
    columnClass: 'center',
    getValue: (row) => row.lifeStage || ''
});
setColumnInfo({
    columnID: QueryColumnID.Problems,
    fullName: 'Import Problems',
    abbrName: null,
    description: 'Problems encountered importing the data from GBIF',
    column1: 'problems',
    options: nullableOptions,
    defaultEmWidth: 16,
    columnClass: null,
    getValue: (row) => row.problems || ''
});
setColumnInfo({
    columnID: QueryColumnID.Phylum,
    fullName: 'Phylum',
    abbrName: null,
    description: 'Phylum determined for the specimen',
    column1: 'phylum_name',
    column2: 'phylum_id',
    options: nullableOptions,
    defaultEmWidth: 8,
    columnClass: null,
    getValue: (row) => row.phylumName || ''
});
setColumnInfo({
    columnID: QueryColumnID.Class,
    fullName: 'Class',
    abbrName: null,
    description: 'Class determined for the specimen',
    column1: 'class_name',
    column2: 'class_id',
    options: nullableOptions,
    defaultEmWidth: 10,
    columnClass: null,
    getValue: (row) => row.className || ''
});
setColumnInfo({
    columnID: QueryColumnID.Order,
    fullName: 'Order',
    abbrName: null,
    description: 'Order determined for the specimen',
    column1: 'order_name',
    column2: 'order_id',
    options: nullableOptions,
    defaultEmWidth: 10,
    columnClass: null,
    getValue: (row) => row.orderName || ''
});
setColumnInfo({
    columnID: QueryColumnID.Family,
    fullName: 'Family',
    abbrName: null,
    description: 'Family determined for the specimen',
    column1: 'family_name',
    column2: 'family_id',
    options: nullableOptions,
    defaultEmWidth: 10,
    columnClass: null,
    getValue: (row) => row.familyName || ''
});
setColumnInfo({
    columnID: QueryColumnID.Genus,
    fullName: 'Genus',
    abbrName: null,
    description: 'Genus determined for the specimen',
    column1: 'genus_name',
    column2: 'genus_id',
    options: nullableOptions,
    defaultEmWidth: 10,
    columnClass: null,
    getValue: (row) => _toItalic(row.genusName)
});
setColumnInfo({
    columnID: QueryColumnID.Subgenus,
    fullName: 'Subgenus',
    abbrName: null,
    description: 'Subgenus determined for the specimen, if any',
    column1: 'subgenus',
    options: nullableOptions,
    defaultEmWidth: 6,
    columnClass: null,
    getValue: (row) => row.subgenus || ''
});
setColumnInfo({
    columnID: QueryColumnID.Species,
    fullName: 'Species Epithet',
    abbrName: 'Species',
    description: 'Specific epithet determined for the specimen',
    column1: 'species_name',
    column2: 'species_id',
    options: nullableOptions,
    defaultEmWidth: 9,
    columnClass: null,
    getValue: (row) => _toItalic(row.speciesName)
});
setColumnInfo({
    columnID: QueryColumnID.Subspecies,
    fullName: 'Subspecies Epithet',
    abbrName: 'Subspecies',
    description: 'Infraspecific epithet determined for the specimen',
    column1: 'subspecies_name',
    column2: 'subspecies_id',
    options: nullableOptions,
    defaultEmWidth: 9,
    columnClass: null,
    getValue: (row) => _toItalic(row.subspeciesName)
});
setColumnInfo({
    columnID: QueryColumnID.TaxonUnique,
    fullName: 'Scientific Name',
    abbrName: null,
    description: 'Most-specific taxon determined, with author and date when available',
    column1: 'taxon_unique',
    // genus_id needed for italics determination
    column2: 'taxon_author, taxon_id, genus_id',
    options: null,
    defaultEmWidth: 12,
    columnClass: null,
    getValue: (row) => {
        const name = row.genusID ? _toItalic(row.taxonUnique) : row.taxonUnique;
        return name + (row.taxonAuthor ? ' ' + row.taxonAuthor : '');
    }
});
setColumnInfo({
    columnID: QueryColumnID.KarstObligate,
    fullName: 'Karst Obligate?',
    abbrName: null,
    description: 'Whether the taxon is stygobitic or troglobitic',
    column1: 'karst_obligate',
    options: [
        { text: 'Any value', sql: null },
        { text: 'Yes', sql: 'X is not null' },
        { text: 'Stygobite', sql: "X='stygobite'" },
        { text: 'Troglobite', sql: "X='troglobite'" },
        { text: 'No', sql: 'X is null' }
    ],
    defaultEmWidth: 8,
    columnClass: 'center',
    getValue: (row) => row.karstObligate || 'no'
});
setColumnInfo({
    columnID: QueryColumnID.IsFederallyListed,
    fullName: 'Federally Listed?',
    abbrName: null,
    description: 'Whether the taxon is a federally listed species',
    column1: 'is_federally_listed',
    options: yesNoOptions,
    defaultEmWidth: 6,
    columnClass: 'center',
    getValue: (row) => (row.isFederallyListed ? 'Yes' : 'No')
});
setColumnInfo({
    columnID: QueryColumnID.TexasStateRank,
    fullName: 'Texas State Rank',
    abbrName: 'State Rank',
    description: 'State of Texas conservation rank',
    column1: 'state_rank',
    options: nullableOptions,
    defaultEmWidth: 6,
    columnClass: 'center',
    getValue: (row) => row.stateRank || ''
});
setColumnInfo({
    columnID: QueryColumnID.TpwdStatus,
    fullName: 'TPWD Status',
    abbrName: null,
    description: 'TPWD conservation status',
    column1: 'tpwd_status',
    options: [
        { text: 'Any value', sql: null },
        { text: 'SGCN', sql: "X='SGCN'" },
        { text: 'Non-blank', sql: 'X is not null' },
        { text: 'Blank', sql: 'X is null' }
    ],
    defaultEmWidth: 6,
    columnClass: 'center',
    getValue: (row) => row.tpwdStatus || ''
});
setColumnInfo({
    columnID: QueryColumnID.County,
    fullName: 'County',
    abbrName: null,
    description: 'County of Texas in which specimen was found',
    column1: 'county_name',
    column2: 'county_id',
    options: nullableOptions,
    defaultEmWidth: 12,
    columnClass: null,
    getValue: (row) => row.countyName || ''
});
setColumnInfo({
    columnID: QueryColumnID.Locality,
    fullName: 'Locality',
    abbrName: null,
    description: 'Locality within county where specimen was found',
    column1: 'locality_name',
    column2: 'locality_id',
    options: null,
    defaultEmWidth: 16,
    columnClass: null,
    getValue: (row) => row.localityName || ''
});
setColumnInfo({
    columnID: QueryColumnID.IsAquaticKarst,
    fullName: 'Aquatic Karst?',
    abbrName: null,
    description: 'Whether the locality is aquatic karst',
    column1: 'is_aquatic_karst',
    options: yesNoOptions,
    defaultEmWidth: 6,
    columnClass: 'center',
    getValue: (row) => (row.isAquaticKarst ? 'Yes' : 'No')
});
setColumnInfo({
    columnID: QueryColumnID.IsTerrestrialKarst,
    fullName: 'Terrestrial Karst?',
    abbrName: null,
    description: 'Whether the locality is terrestrial karst',
    column1: 'is_terrestrial_karst',
    options: yesNoOptions,
    defaultEmWidth: 6,
    columnClass: 'center',
    getValue: (row) => (row.isTerrestrialKarst ? 'Yes' : 'No')
});
setColumnInfo({
    columnID: QueryColumnID.Latitude,
    fullName: 'Latitude',
    abbrName: null,
    description: 'Latitude of cave at which specimen was found',
    column1: 'latitude',
    options: nullableOptions,
    defaultEmWidth: 6,
    columnClass: null,
    getValue: (row) => getNumber(row.latitude)
});
setColumnInfo({
    columnID: QueryColumnID.Longitude,
    fullName: 'Longitude',
    abbrName: null,
    description: 'Longitude of cave at which specimen was found',
    column1: 'longitude',
    options: nullableOptions,
    defaultEmWidth: 6,
    columnClass: null,
    getValue: (row) => getNumber(row.longitude)
});
function _toItalic(name) {
    return name ? `<i>${name}</i>` : '';
}
function _toDisplayedPartialDate(partialDate) {
    const dashOffset = partialDate.indexOf('-');
    if (dashOffset < 0)
        return partialDate; // just the year
    const year = partialDate.substring(0, dashOffset);
    const month = partialDate.substring(dashOffset + 1);
    // parse the month to strip any leading zero
    return `${parseInt(month)}/${year}`;
}
